import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ToastProvider } from './contexts/ToastContext';

import Dashboard from './pages/Dashboard';
import Employees from './pages/Employees';
import Approvals from './pages/Approvals';
import Transactions from './pages/Transactions';
import Reports from './pages/Reports';
import Settings from './pages/Settings';
import SignupCodes from './pages/SignupCodes';

import LoginPage from './pages/auth/LoginPage';
import EmployerLoginPage from './pages/auth/EmployerLoginPage';
import TwoFactorSetupPage from './pages/auth/TwoFactorSetupPage';
import ForgotPasswordPage from './pages/auth/ForgotPasswordPage';
import AdminSetupPage from './pages/auth/AdminSetupPage';
import OnboardPage from './pages/auth/OnboardPage';
import OnboardSubmittedPage from './pages/auth/OnboardSubmittedPage';
import ResetPasswordPage from './pages/auth/ResetPasswordPage';
import LoginLandingPage from './pages/auth/LoginLandingPage';

import Layout from './components/Layout';

const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading, user, company } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading authentication...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated || !user || !company) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

const RootRedirect: React.FC = () => {
  return <Navigate to="/" replace />;
};

const AppRoutes: React.FC = () => {
  return (
    <Router>
      <Routes>
        <Route path="/" element={<LoginLandingPage />} />
        <Route path="/login" element={<EmployerLoginPage />} />
        <Route path="/onboard" element={<OnboardPage />} />
        <Route path="/onboard/submitted" element={<OnboardSubmittedPage />} />

        <Route path="/client/:companySlug/admin/login" element={<LoginPage />} />
        <Route path="/client/:companySlug/admin/2fa-setup" element={<TwoFactorSetupPage />} />
        <Route path="/client/:companySlug/admin/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/client/:companySlug/admin/reset-password" element={<ResetPasswordPage />} />
        <Route path="/client/:companySlug/admin/setup" element={<AdminSetupPage />} />

        <Route path="/client/:companySlug/admin" element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }>
          <Route index element={<Navigate to="dashboard" replace />} />
          <Route path="dashboard" element={<Dashboard />} />
          <Route path="employees" element={<Employees />} />
          <Route path="signup-codes" element={<SignupCodes />} />
          <Route path="approvals" element={<Approvals />} />
          <Route path="transactions" element={<Transactions />} />
          <Route path="reports" element={<Reports />} />
          <Route path="settings" element={<Settings />} />
        </Route>

        <Route path="*" element={<RootRedirect />} />
      </Routes>
    </Router>
  );
};

const App: React.FC = () => {
  return (
    <AuthProvider>
      <ToastProvider>
        <AppRoutes />
      </ToastProvider>
    </AuthProvider>
  );
};

export default App;
